// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { SearchParams } from '@api/constants';
import { NavigationBar } from '@imprivata-cloud/design-system';
import { EamcPath, LegacyEamcPath, PortalPath } from '@portal/constants';
import Settings from '@portal/containers/settings/Settings';
import { EAMC_ACCESS, useIsAvailable } from '@portal/containers/settings/SettingsRegistry';
import { type PageItem, SETTINGS_ITEMS } from '@portal/containers/settings/SettingsTabs';
import { omitSearchParam, preserveCurrentSearch } from '@utils/navigationSearch';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, matchPath, Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { EamcChatbotGate } from '@/eamcChatbot/EamcChatbotGate';
import '../settings/Settings.less';

type EamcPage = Extract<PageItem, { owner: 'eamc' | 'both' }>;

const isEamcPage = (item: PageItem): item is EamcPage => item.owner !== 'global';

export const EAMC_PAGES = SETTINGS_ITEMS.filter(isEamcPage);

export const EAMC_LEGACY_REDIRECTS = [
  { from: LegacyEamcPath.SECURITY, to: EamcPath.WORKFLOWS },
] as const;

export const EamcRedirect = () => {
  const [searchParams] = useSearchParams();
  return <Navigate to={preserveCurrentSearch(EamcPath.ENTRA_ID_USERS, searchParams)} replace />;
};

export const EamcItemRoute = ({ item }: { item: EamcPage }) => {
  const [searchParams] = useSearchParams();
  const available = useIsAvailable();

  if (!available(item.gate)) {
    return <Navigate to={preserveCurrentSearch(PortalPath.EAMC, searchParams)} replace />;
  }
  return <Settings item={item} />;
};

const EamcLayout = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const available = useIsAvailable();
  const productName = t('portal.eamc.product-name');

  useEffect(() => {
    const previous = document.title;
    document.title = productName;
    return () => {
      document.title = previous;
    };
  }, [productName]);

  if (!available(EAMC_ACCESS)) {
    return <Navigate to={preserveCurrentSearch(PortalPath.HOME, searchParams)} replace />;
  }

  const pages = EAMC_PAGES.filter(page => available(page.gate));
  const users = pages.filter(page => page.navGroup === 'users');
  const links = pages.filter(page => page.navGroup === undefined);
  const isActive = (page: EamcPage) => matchPath(page.routes.eamc, pathname) !== null;
  const navSearch = omitSearchParam(
    omitSearchParam(searchParams, SearchParams.REPORT),
    SearchParams.EDIT_APP,
  );
  const linkTo = (page: EamcPage) => preserveCurrentSearch(page.routes.eamc, navSearch);
  const label = (page: EamcPage) => t(page.navLabelKey ?? page.titleKey);

  return (
    <>
      <div className={'settings-nav-bar'}>
        <NavigationBar>
          <NavigationBar.Start>
            <NavigationBar.Group>
              {users.length > 0 && (
                <NavigationBar.PopItem
                  text={t('portal.settings.tabs-title.users')}
                  aria-current={users.some(isActive) ? 'true' : undefined}
                >
                  <div className="flex flex-col w-50">
                    {users.map(page => (
                      <NavigationBar.SubmenuItem
                        key={page.key}
                        active={isActive(page)}
                        render={props => <Link {...props} to={linkTo(page)} />}
                      >
                        {label(page)}
                      </NavigationBar.SubmenuItem>
                    ))}
                  </div>
                </NavigationBar.PopItem>
              )}
              {links.map(page => (
                <NavigationBar.LinkItem
                  key={page.key}
                  active={isActive(page)}
                  render={props => <Link {...props} to={linkTo(page)} />}
                >
                  {label(page)}
                </NavigationBar.LinkItem>
              ))}
            </NavigationBar.Group>
          </NavigationBar.Start>
        </NavigationBar>
      </div>
      <Outlet />
      <EamcChatbotGate />
    </>
  );
};

export default EamcLayout;
